import { useEffect, useState } from "react";
import { Button, Drawer, Heading, HeadingRow, Pagination, Treegrid, StatusBlock } from "cai-fusion";
import { useNavigate } from "react-router-dom";

// Side menu icons
import { ReactComponent as IconFolder } from "../../../images/icon-folder.svg"
import { ReactComponent as IconLike } from "../../../images/icon-like.svg"
import { ReactComponent as IconReview } from "../../../images/icon-review.svg"
import { useSiftV2 } from "../contexts/siftV2Context";


const FilterListItem = ({id, name, tabState, amount, onClick, SVGObject}) => {

    return (
        <li className={`m-vertical-tabs__item ${tabState === id ? "m-vertical-tabs__item--active" : ""}`}
            id={`tab${id}`}
            role="tab"
            tabIndex={id}
            aria-controls={`tabpanel${id}`}
            aria-selected="true"
        >
            <a className="m-vertical-tabs__link" onClick={onClick}>
                <div className="m-vertical-tabs__icon-wrapper">
                    <SVGObject className="m-vertical-tabs__icon"/>
                </div>
                <span className="m-vertical-tabs__label">{name}</span>
                {amount > 0 && <span className="a-badge m-vertical-tabs__badge">{amount}</span>}
            </a>
        </li>
    )
}


const OpportunityItem = ({opportunityObj}) => {

    const navigate = useNavigate();

    let indicatorColors = {
        "Draft": "gray",
        "Pre-Qualification": "blue",
        "SME Review": "yellow",
        "Bid": "green",
        "No Bid": "red",
    }

    return (
        <tr className="o-treegrid__row o-treegrid__row--shown">
            {/* Checkbox */}
            <td className="o-treegrid__cell o-treegrid__cell--centered">
            </td>
            {/* Name & Description */}
            <td className="o-treegrid__cell">
                
                <div id="topic-1" className="m-subject-highlight">
                    <div className="m-subject-highlight__content">    
                        <p className="a-bold-text m-subject-highlight__title">{opportunityObj?.columnData?.title}</p>
                        <p className="a-muted-text m-subject-highlight__subtitle">{opportunityObj?.source}</p>
                    </div>
                </div>
            </td>
            {/* Date */}
            <td className="o-treegrid__cell">{opportunityObj?.columnData?.dateRequested}</td>
            {/* Assignment Name */}
            <td className="o-treegrid__cell">{opportunityObj?.currentAssignment}</td>
            {/* Progress Indicator */}
            <td className="o-treegrid__cell">
                <StatusBlock 
                    indicatorColor={indicatorColors[opportunityObj?.columnData?.currentState] ?? "gray"}
                    label={opportunityObj?.columnData?.currentState}
                />
            </td>
            {/* ...Actions? */}
            <td className="o-treegrid__cell o-treegrid__cell--actions">
                <a 
                    className="a-button a-button--outline a-button--small o-treegrid__hidden-button"
                    onClick={() => navigate(`/${process.env.REACT_APP_BRANDING_SIFT_PATH ?? "sift"}/opportunities/${opportunityObj?.id ?? 'fart'}`)}
                >
                    Details
                </a>
            </td>
        </tr>
    )
}


const SiftListOpportunities = () => {

    const [filterType, setFilterType] = useState("");
    const [page, setPage] = useState(1);

    const navigate = useNavigate();

    const { opportunities } = useSiftV2();

    const [filteredOpps, setFilteredOpps] = useState([]);

    useEffect(() => {
        // A check to update pagination as needed - if we're on a page that no longer exists, go back one.
        if (page > 1 && filteredOpps.length <= (page - 1) * 10) {
            setPage(page - 1);
        }
    }, [filteredOpps])

    useEffect(() => {
        if (filterType === "") {
            setFilteredOpps([...opportunities.filter((opp) => (opp?.columnData?.currentState !== "Bid" && opp?.columnData?.currentState !== "No Bid" && opp?.columnData?.currentState !== "Archived"))]);
        } else {
            setFilteredOpps([...opportunities.filter((opp) => opp?.columnData?.currentState === filterType)])
        }
    }, [opportunities, filterType])

    return (
        <div className="o-page-section o-page-section l-pt-none">
            <div className="o-page-section__content-container o-page-section__content-container--wide-fixed-width l-pt-none l-mt-neg-sm">
                <div className="l-row l-pt-sm">
                    <Drawer
                        className="o-drawer--left-narrow col-2"
                        type="hybrid"
                        header={
                            <div className="o-drawer-heading-row">
                                <div className="o-drawer-title">
                                    <Heading size={3}>Status</Heading>
                                </div>
                            </div>
                        }
                        children={
                            <nav className="m-vertical-tabs">
                                <ul className="m-vertical-tabs__list" role="tablist" aria-orientation="vertical">
                                    <FilterListItem SVGObject={IconFolder} tabState={filterType} amount={opportunities.filter((opp) => (opp?.columnData?.currentState !== "Bid" && opp?.columnData?.currentState !== "No Bid" && opp?.columnData?.currentState !== "Archived")).length} name="All WIP" id="" onClick={() => setFilterType("")} />
                                    <FilterListItem SVGObject={IconReview} tabState={filterType} amount={opportunities.filter((opp) => opp?.columnData?.currentState === "Draft").length} name="Draft" id="Draft" onClick={() => setFilterType("Draft")} />
                                    <FilterListItem SVGObject={IconLike} tabState={filterType} amount={opportunities.filter((opp) => opp?.columnData?.currentState === "Pre-Qualification").length} name="Pre-qual" id="Pre-qualification" onClick={() => setFilterType("Pre-Qualification")} />
                                    <FilterListItem SVGObject={IconLike} tabState={filterType} amount={opportunities.filter((opp) => opp?.columnData?.currentState === "SME Review").length} name="SME Review" id="SME Review" onClick={() => setFilterType("SME Review")} />
                                    <FilterListItem SVGObject={IconFolder} tabState={filterType} amount={opportunities.filter((opp) => opp?.columnData?.currentState === "Bid").length} name="Bid" id="Bid" onClick={() => setFilterType("Bid")} />
                                    <FilterListItem SVGObject={IconFolder} tabState={filterType} amount={opportunities.filter((opp) => opp?.columnData?.currentState === "No Bid").length} name="No Bid" id="No Bid" onClick={() => setFilterType("No Bid")} />
                                    <FilterListItem SVGObject={IconFolder} tabState={filterType} amount={opportunities.filter((opp) => opp?.columnData?.currentState === "Archived").length} name="Archived" id="Archived" onClick={() => setFilterType("Archived")} />
                                </ul>
                            </nav>
                        }
                    />
                    <div className="col-md-10">
                        <HeadingRow 
                            className="l-mb-sm l-pb-xs"
                            title="All Opportunities"
                            isSecondary
                            children={
                                <Button children="Add Opportunity" onClick={() => navigate(`/${process.env.REACT_APP_BRANDING_SIFT_PATH ?? "sift"}/opportunities/new`)}/>
                            }
                        />
                        <div className="o-block">
                            {filteredOpps.length === 0
                            ?   <div className="text-center" aria-colspan={4}>
                                    <p className="a-muted-text l-mt-xs">{`You have no opportunities available${filterType === "" ? "" : " that fit this criteria"}. Create one using the button above!`}</p>
                                </div>
                            :   <Treegrid
                                    columnSizes={["48", "large", "medium", "medium", "wide", "small"]}
                                    headings={["", "Opportunity", "Created Date", "Currently Assigned", "Status", ""]}
                                >
                                    {/* Take a slice based on the current page. If we're at the end of the list, we don't want to access indexes out of bounds. */}
                                    {filteredOpps.slice((page - 1) * 10, Math.min(filteredOpps.length, page * 10)).map((opportunity) => (
                                        <OpportunityItem status={opportunity?.status ?? "Bid"} opportunityObj={opportunity} key={opportunity?.id} />
                                    ))}
                                </Treegrid>
                            }
                        </div>
                        <Pagination 
                            numPerPage={10}
                            data={filteredOpps}
                            currentPage={page}
                            onPageChange={(page) => setPage(page)}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SiftListOpportunities;